import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Facebook, ShoppingCart, Search, User } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { DnaHelix } from './DnaHelix';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5be9d9ba-e756-4119-8306-729d4aea9d0d/artifacts/84tjmkhw_B3C5DCF3-B9AE-4C3A-8AF4-244C055AD917.png";

const DnaIcon = () => (
  <div className="dna-spin-3d" style={{ perspective: '200px' }}>
    <svg viewBox="0 0 24 32" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ animation: 'dnaSpin3d 3s linear infinite' }}>
      <path d="M8 2 C8 8 16 12 16 18 C16 24 8 28 8 30" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M16 2 C16 8 8 12 8 18 C8 24 16 28 16 30" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" />
      <line x1="8" y1="6" x2="16" y2="6" stroke="#C41E3A" strokeWidth="1.2" opacity="0.7" />
      <line x1="9" y1="10" x2="15" y2="10" stroke="white" strokeWidth="1.2" opacity="0.7" />
      <line x1="9" y1="14" x2="15" y2="14" stroke="#C41E3A" strokeWidth="1.2" opacity="0.7" />
      <line x1="9" y1="18" x2="15" y2="18" stroke="white" strokeWidth="1.2" opacity="0.7" />
      <line x1="9" y1="22" x2="15" y2="22" stroke="#C41E3A" strokeWidth="1.2" opacity="0.7" />
      <line x1="8" y1="26" x2="16" y2="26" stroke="white" strokeWidth="1.2" opacity="0.7" />
    </svg>
    <style>{`
      @keyframes dnaSpin3d {
        0% { transform: rotateY(0deg); }
        100% { transform: rotateY(360deg); }
      }
    `}</style>
  </div>
);

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allPeptides, setAllPeptides] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { user } = useAuth();
  const searchDropdownRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/peptides`).then(res => setAllPeptides(res.data)).catch(() => {});
  }, []);

  const normalize = (str) => str.toLowerCase().replace(/[-_]/g, ' ').replace(/[^a-z0-9 ]/g, '');

  const navSearchSuggestions = searchQuery.trim().length > 0
    ? allPeptides.filter(p => {
        const query = normalize(searchQuery);
        const words = query.split(/\s+/).filter(w => w.length > 0);
        const text = normalize(`${p.name} ${p.dosage} ${p.category} ${p.id}`);
        return words.every(w => text.includes(w));
      }).slice(0, 6)
    : [];

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'PEPTIDES', path: '/peptides' },
    { name: 'ABOUT', path: '/about' },
    { name: 'PRODUCTS', path: '/#products' },
    { name: 'CONTACT', path: 'mailto:canadianwellness2026@outlook.com' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path.startsWith('mailto')) return false;
    return location.pathname.startsWith(path.replace('/#', ''));
  };

  const handleNavClick = (path) => {
    setIsOpen(false);
    if (path.includes('#')) {
      const element = document.getElementById(path.split('#')[1]);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
      setIsOpen(false);
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-black" data-testid="navbar" style={{ borderBottom: '1px solid rgba(196,30,58,0.3)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[80px]">

          {/* Logo - Left: 3D spinning DNA helix */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="navbar-logo">
            <DnaHelix size="md" />
          </Link>

          {/* Desktop Navigation - Center + Right */}
          <div className="hidden md:flex items-center gap-9">
            {navLinks.map((link) =>
              link.path.startsWith('mailto') ? (
                <a
                  key={link.name}
                  href={link.path}
                  className="text-[13px] font-medium uppercase tracking-[0.18em] text-gray-300 hover:text-white transition-colors"
                  data-testid="nav-contact-link"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`text-[13px] font-medium uppercase tracking-[0.18em] transition-colors ${
                    isActive(link.path)
                      ? 'text-white border-b border-white pb-0.5'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            )}

            {/* Account */}
            <Link
              to="/account"
              className={`transition-colors ${user ? 'text-[#D4AF37] hover:text-white' : 'text-gray-300 hover:text-white'}`}
              data-testid="nav-account-button"
              aria-label="My Account"
              title={user ? user.email : 'Sign in'}
            >
              <User size={18} />
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              className="relative text-gray-300 hover:text-white transition-colors"
              data-testid="nav-cart-button"
            >
              <ShoppingCart size={18} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2.5 bg-[#C41E3A] text-white text-[10px] font-bold w-[18px] h-[18px] rounded-full flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Search */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="text-gray-300 hover:text-white transition-colors"
              data-testid="nav-search-toggle"
              aria-label="Search"
            >
              <Search size={18} />
            </button>

            {/* Facebook */}
            <a
              href="https://www.facebook.com/share/g/18dFmzugdi/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-[#1877F2] transition-colors"
              data-testid="nav-facebook-link"
              aria-label="Facebook"
            >
              <Facebook size={18} />
            </a>

            {/* Contact Us - Red border button like reference "Log In" */}
            <a
              href="mailto:canadianwellness2026@outlook.com"
              className="text-white text-[13px] font-medium uppercase tracking-[0.15em] px-5 py-2 border border-[#C41E3A] hover:bg-[#C41E3A]/20 transition-all duration-300 rounded-sm"
              data-testid="nav-contact-button"
            >
              Contact Us
            </a>
          </div>

          {/* Mobile: Account + Cart + Search + Menu */}
          <div className="md:hidden flex items-center gap-4">
            <Link
              to="/account"
              className={`transition-colors ${user ? 'text-[#D4AF37]' : 'text-gray-300 hover:text-white'}`}
              data-testid="mobile-account-button"
              aria-label="My Account"
            >
              <User size={22} />
            </Link>
            <button
              onClick={() => { setSearchOpen(!searchOpen); setIsOpen(false); }}
              className="text-gray-300 hover:text-white transition-colors"
              data-testid="mobile-search-toggle"
            >
              <Search size={22} />
            </button>
            <Link
              to="/cart"
              className="relative text-gray-300 hover:text-white transition-colors"
              data-testid="mobile-cart-button"
            >
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#C41E3A] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-1 text-gray-300 hover:text-white"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Search Dropdown with Live Suggestions */}
        {searchOpen && (
          <div className="absolute left-0 right-0 top-full bg-black border-b border-[#27272A] px-4 py-3 z-50" data-testid="search-dropdown" ref={searchDropdownRef}>
            <form onSubmit={handleSearch} className="max-w-xl mx-auto flex gap-2">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
                className="flex-1 bg-[#121212] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-2 rounded-sm outline-none transition-colors"
                data-testid="nav-search-input"
              />
              <button
                type="submit"
                className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-sm transition-colors"
                data-testid="nav-search-submit"
              >
                <Search size={16} />
              </button>
              <button
                type="button"
                onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                className="text-gray-400 hover:text-white px-2 transition-colors"
              >
                <X size={18} />
              </button>
            </form>
            {/* Live Suggestions */}
            {navSearchSuggestions.length > 0 && (
              <div className="max-w-xl mx-auto mt-2 bg-[#121212] border border-[#27272A] rounded-md overflow-hidden" data-testid="nav-search-suggestions">
                {navSearchSuggestions.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#1a1a1a] transition-colors border-b border-[#27272A] last:border-b-0"
                    data-testid={`nav-suggestion-${p.id}`}
                  >
                    <img src={p.image} alt={p.name} className="w-9 h-9 object-contain rounded bg-white/5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{p.name}</p>
                      <p className="text-gray-500 text-xs">{p.dosage}</p>
                    </div>
                    <span className="text-[#C41E3A] text-sm font-bold">{p.price}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Mobile Dropdown */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-[#27272A]" data-testid="mobile-menu">
            {navLinks.map((link) =>
              link.path.startsWith('mailto') ? (
                <a
                  key={link.name}
                  href={link.path}
                  className="block py-3 text-sm font-semibold uppercase tracking-widest text-gray-300 hover:text-[#C41E3A] transition-colors"
                  data-testid="mobile-contact-link"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`block py-3 text-sm font-semibold uppercase tracking-widest transition-colors ${
                    isActive(link.path)
                      ? 'text-[#C41E3A]'
                      : 'text-gray-300 hover:text-[#C41E3A]'
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            )}
            <Link
              to="/account"
              onClick={() => setIsOpen(false)}
              className="block py-3 text-sm font-semibold uppercase tracking-widest text-gray-300 hover:text-[#C41E3A] transition-colors"
              data-testid="mobile-nav-account"
            >
              {user ? 'MY ACCOUNT' : 'SIGN IN'}
            </Link>
            <Link
              to="/cart"
              onClick={() => setIsOpen(false)}
              className="block py-3 text-sm font-semibold uppercase tracking-widest text-gray-300 hover:text-[#C41E3A] transition-colors"
              data-testid="mobile-nav-cart"
            >
              CART {cartCount > 0 && `(${cartCount})`}
            </Link>
            <a
              href="https://www.facebook.com/share/g/18dFmzugdi/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 py-3 text-sm font-semibold uppercase tracking-widest text-gray-300 hover:text-[#1877F2] transition-colors"
              data-testid="mobile-facebook-link"
            >
              <Facebook size={18} />
              FACEBOOK
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
